import {
  ASK_USER_SKIP_SENTINEL,
  type MentionEntity,
  type MessageSegment,
  type PromptOption,
} from '@terra/shared-dto-chat-types';
import { toRecord } from '../shared/network-tool-card-utils';

function parseRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    try {
      return toRecord(JSON.parse(trimmed));
    } catch {
      return undefined;
    }
  }

  return toRecord(value);
}

function isMentionEntity(value: unknown): value is MentionEntity {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as {
    entityId?: unknown;
    entityType?: unknown;
    label?: unknown;
  };
  return (
    typeof record.entityId === 'string' &&
    typeof record.entityType === 'string' &&
    typeof record.label === 'string'
  );
}

function isMessageSegment(value: unknown): value is MessageSegment {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as {
    type?: unknown;
    value?: unknown;
    entityId?: unknown;
    entityType?: unknown;
    label?: unknown;
  };
  if (record.type === 'text') {
    return typeof record.value === 'string';
  }
  if (record.type === 'mention') {
    return (
      typeof record.entityId === 'string' &&
      typeof record.entityType === 'string' &&
      typeof record.label === 'string'
    );
  }
  return false;
}

function readAskUserAnswerMentions(value: unknown): MentionEntity[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isMentionEntity);
}

function readAskUserAnswerSegments(value: unknown): MessageSegment[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isMessageSegment);
}

export function readAskUserPrompt(input: unknown): string {
  const record = parseRecord(input);
  return typeof record?.prompt === 'string' ? record.prompt : '';
}

function readPromptOption(value: unknown): PromptOption | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as {
    value?: unknown;
    label?: unknown;
    id?: unknown;
  };
  const label = typeof record.label === 'string' ? record.label : '';
  if (!label) {
    return null;
  }

  if (typeof record.value === 'string') {
    return { value: record.value, label };
  }

  if (typeof record.id === 'string') {
    return { value: record.id, label };
  }

  return { value: label, label };
}

export function readAskUserOptions(input: unknown): PromptOption[] {
  const record = parseRecord(input);
  if (!Array.isArray(record?.options)) {
    return [];
  }

  return record.options
    .map(readPromptOption)
    .filter((option): option is PromptOption => option !== null);
}

export function readAskUserAnswer(output: unknown): {
  answer: string;
  skipped: boolean;
  answerMentions: MentionEntity[];
  answerSegments: MessageSegment[];
} {
  const record = parseRecord(output);
  if (!record) {
    return {
      answer: '',
      skipped: false,
      answerMentions: [],
      answerSegments: [],
    };
  }
  if (record.skipped === true) {
    return {
      answer: '',
      skipped: true,
      answerMentions: [],
      answerSegments: [],
    };
  }
  const selectedLabel =
    typeof record.selectedLabel === 'string' ? record.selectedLabel : '';
  return {
    answer: selectedLabel,
    skipped: selectedLabel === ASK_USER_SKIP_SENTINEL,
    answerMentions: readAskUserAnswerMentions(record.answerMentions),
    answerSegments: readAskUserAnswerSegments(record.answerSegments),
  };
}
